import React, { Suspense, lazy, useContext } from 'react';

import { Navigate, Routes, Route } from 'react-router-dom';
import { map } from 'lodash';

import { useSnackbar } from 'notistack';

import { Container } from '@mui/material';

import { Profile } from 'src/types/models';
import { isTrinityUser } from 'src/utils/user';

import {
  MOCK_RESOLVE_PATH,
  mockResolveRouteEnabled,
} from 'src/features/merge-resolution/config';
import ResolveConflictsScreen from 'src/features/merge-resolution/components/ResolveConflictsScreen';

import EditAssumptionsDetail from '../ScenarioManager/EditAssumptionsDetail';

import OutputsPanel from '../ScenarioManager/EditAssumptionsDetail/OutputsPanel';

import EditGroupPermissions from '../Admin/EditGroupPermissions';

import RouteParams from './RouteParams';
import ProtectedRoute from './ProtectedRoute';
import Unauthorized from './Unauthorized';
import Loading from './Loading';
import { ProfileContext } from './AuthController';

// Loaded on demand so the fixtures-only harness, its mock data source and its
// fixtures land in a separate chunk rather than the main bundle. The chunk is
// fetched only when the gated route below is actually rendered.
const MockResolveHarness = lazy(
  () => import('src/features/merge-resolution/components/MockResolveHarness')
);

const RoutesComponent = () => {
  const { enqueueSnackbar } = useSnackbar();

  const handleInvalidRoute = (path: string, label: string) => {
    enqueueSnackbar(
      `Your company admin for CloudCast has not given you permission to
                access the ${label} page. Please reach out to them
                if you think you should have access.`,
      { variant: 'error' }
    );
  };

  const profile = useContext(ProfileContext);
  if (profile === undefined) {
    throw Error('Profile is required to render NavigationBar');
  }

  const showForAdmin = isTrinityUser((profile as Profile).User);

  return (
    <Routes>
      {map(RouteParams().routes, (route) => {
        if (!route.protected) {
          return (
            <Route
              key={route.pathName}
              path={`${route.pathName}`}
              element={route.component}
            />
          );
        }
        return (
          <ProtectedRoute
            path={`${route.pathName}`}
            label={route.label}
            key={route.pathName}
            callback={handleInvalidRoute}
          />
        );
      })}
      <Route
        path="/scenario-manager/assumptions"
        element={<EditAssumptionsDetail />}
      />
      <Route
        path="/scenario-manager/assumptions/outputs"
        element={
          <Container maxWidth={false}>
            <OutputsPanel inContainer />
          </Container>
        }
      />
      <Route
        path="/scenario-manager/resolve-conflicts"
        element={<ResolveConflictsScreen />}
      />
      {/*
        Fixtures-only review harness for the resolve screen. Gated on both the
        build-time flag and the Trinity-only check - see
        mockResolveRouteEnabled. When it is off the path is not registered at
        all, so it falls through to the catch-all redirect below exactly as an
        unknown path would.
      */}
      {mockResolveRouteEnabled((profile as Profile).User) && (
        <Route
          path={MOCK_RESOLVE_PATH}
          element={
            <Suspense fallback={<Loading />}>
              <MockResolveHarness />
            </Suspense>
          }
        />
      )}
      {showForAdmin && (
        <Route
          path="/admin/edit-group-permissions"
          element={<EditGroupPermissions />}
        />
      )}
      <Route path="/unauthorized" element={<Unauthorized />} />

      <Route
        path="*"
        element={<Navigate to={RouteParams().defaultRoute.pathName} />}
      />
    </Routes>
  );
};

export default RoutesComponent;
